import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { auth } from '../api/endpoints';
import { FIXTURE_MODE } from '../mock/install';

const ERROR_MESSAGES: Record<string, string> = {
  entra_domain: 'Use your chs.agency Microsoft account.',
  entra_failed: 'Microsoft sign-in failed. Try again.',
};

export function Login() {
  const { user, login } = useSession();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string }; search: string };
  const [email, setEmail] = useState(FIXTURE_MODE ? 'client@example.com' : '');
  const [password, setPassword] = useState(FIXTURE_MODE ? 'proofroom-client' : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [entra, setEntra] = useState(false);

  useEffect(() => {
    auth.methods().then((m) => setEntra(m.entra)).catch(() => {});
  }, []);

  useEffect(() => {
    const code = new URLSearchParams(loc.search).get('error');
    if (code && ERROR_MESSAGES[code]) setError(ERROR_MESSAGES[code]);
  }, [loc.search]);

  if (user) return <Navigate to={loc.state?.from ?? '/projects'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await login(email.trim(), password);
      nav(loc.state?.from ?? '/projects', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="login">
      <div className="card">
        <h1>Proofroom</h1>
        <p className="sub">Sign in to review your proofs</p>
        {entra && (
          <>
            <a className="btn" href="/api/auth/entra/start" style={{ width: '100%', height: 36, justifyContent: 'center' }}>Sign in with Microsoft</a>
            <p className="muted small" style={{ textAlign: 'center', margin: '16px 0' }}>or</p>
          </>
        )}
        <form onSubmit={submit}>
          <div className="field">
            <label className="label" htmlFor="email">Email</label>
            <input id="email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label className="label" htmlFor="password">Password</label>
            <input id="password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <p className="error-text" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
          <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', height: 36, justifyContent: 'center' }}>{busy ? 'Signing in' : 'Sign in'}</button>
          {FIXTURE_MODE && <p className="muted small" style={{ marginTop: 16 }}>Fixture mode. Seed accounts: client@example.com / proofroom-client, designer@chs.agency / proofroom-designer, chris.southgate@chs.agency / proofroom-admin.</p>}
        </form>
      </div>
    </div>
  );
}
